import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useOrderStore } from './orderStore';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  primaryRole?: string;
  roles: string[];
  permissions: string[];
}

interface Restaurant {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  settings: Record<string, any>;
}

interface LoginResult {
  success: boolean;
  error?: string;
}

interface AuthState {
  token: string | null;
  employee: Employee | null;
  restaurant: Restaurant | null;
  isAuthenticated: boolean;
  // True when the last sign-out was the session expiring (not the user's
  // choice) — the login screen explains it calmly. Never persisted.
  sessionExpired: boolean;
  selectedRestaurantId: string | null;
  selectedRestaurantName: string | null;
  isLoadingRestaurants: boolean;
  login: (pin: string) => Promise<LoginResult>;
  logout: (expired?: boolean) => void;
  getRestaurantId: () => string | null;
  fetchRestaurantBySlug: (slugOrId: string) => Promise<boolean>;
  selectRestaurant: (id: string, name: string) => void;
  clearRestaurant: () => void;
}

// Get restaurant info from localStorage
const getStoredRestaurant = (): { id: string | null; name: string | null } => {
  const id = localStorage.getItem('mise-pos-restaurant-id');
  const name = localStorage.getItem('mise-pos-restaurant-name');
  return { id, name };
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      employee: null,
      restaurant: null,
      isAuthenticated: false,
      sessionExpired: false,
      selectedRestaurantId: getStoredRestaurant().id,
      selectedRestaurantName: getStoredRestaurant().name,
      isLoadingRestaurants: false,

      fetchRestaurantBySlug: async (slugOrId: string) => {
        set({ isLoadingRestaurants: true });
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
          const response = await fetch(`${apiUrl}/v1/restaurants/lookup/${encodeURIComponent(slugOrId)}`);

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              const { id, name } = data.data;
              localStorage.setItem('mise-pos-restaurant-id', id);
              localStorage.setItem('mise-pos-restaurant-name', name);
              set({ selectedRestaurantId: id, selectedRestaurantName: name, isLoadingRestaurants: false });
              return true;
            }
          }
          set({ isLoadingRestaurants: false });
          return false;
        } catch (error) {
          console.error('Failed to lookup restaurant:', error);
          set({ isLoadingRestaurants: false });
          return false;
        }
      },

      selectRestaurant: (id: string, name: string) => {
        localStorage.setItem('mise-pos-restaurant-id', id);
        localStorage.setItem('mise-pos-restaurant-name', name);
        set({ selectedRestaurantId: id, selectedRestaurantName: name });
      },

      clearRestaurant: () => {
        localStorage.removeItem('mise-pos-restaurant-id');
        localStorage.removeItem('mise-pos-restaurant-name');
        set({ selectedRestaurantId: null, selectedRestaurantName: null });
      },

      login: async (pin: string): Promise<LoginResult> => {
        try {
          const restaurantId = get().selectedRestaurantId;

          if (!restaurantId) {
            return { success: false, error: 'Please select a restaurant first.' };
          }

          console.log('Attempting PIN login with restaurantId:', restaurantId);
          const body = { pin, restaurantId };

          const apiUrl = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
          console.log('API URL:', apiUrl);

          const response = await fetch(`${apiUrl}/v1/auth/pin-login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });

          const data = await response.json();
          console.log('Login response:', response.status, data);

          if (!response.ok || !data.success) {
            return { success: false, error: data.error?.message || 'Invalid PIN' };
          }

          // Store the restaurant ID for future requests
          if (data.data.restaurant?.id) {
            localStorage.setItem('mise-pos-restaurant-id', data.data.restaurant.id);
          }

          set({
            token: data.data.accessToken,
            employee: {
              ...data.data.employee,
              primaryRole: data.data.employee?.primaryRole || data.data.employee?.role,
            },
            restaurant: data.data.restaurant,
            isAuthenticated: true,
            sessionExpired: false,
          });

          // Set tax rate from restaurant settings. Two shapes exist:
          // settings.tax.defaultRate (fraction) and settings.defaultTaxRate
          // (percent — what the manager Settings page writes). > 1 = percent.
          const settings = data.data.restaurant?.settings as Record<string, any> || {};
          const rawRate = typeof settings.tax?.defaultRate === 'number'
            ? settings.tax.defaultRate
            : typeof settings.taxRate === 'number'
              ? settings.taxRate
              : settings.defaultTaxRate;
          let taxRate = typeof rawRate === 'number' && rawRate >= 0 ? rawRate : 0.0875;
          if (taxRate > 1) taxRate = taxRate / 100;
          useOrderStore.getState().setTaxRate(taxRate);

          return { success: true };
        } catch (error: any) {
          console.error('Login error:', error);
          return { success: false, error: error.message || 'Network error. Is the API running?' };
        }
      },

      logout: (expired = false) => {
        set({
          token: null,
          employee: null,
          restaurant: null,
          isAuthenticated: false,
          sessionExpired: expired,
        });
      },

      getRestaurantId: () => {
        return get().restaurant?.id || get().selectedRestaurantId;
      },
    }),
    {
      name: 'mise-pos-auth',
      partialize: (state) => ({
        token: state.token,
        employee: state.employee,
        restaurant: state.restaurant,
        isAuthenticated: state.isAuthenticated,
        selectedRestaurantId: state.selectedRestaurantId,
        selectedRestaurantName: state.selectedRestaurantName,
      }),
      // A restored session whose token has expired must land on the login
      // screen once, cleanly — not pretend to be signed in from cache and
      // then boot the user mid-action on the first real request.
      onRehydrateStorage: () => (state) => {
        if (!state?.token) return;
        try {
          const { exp } = JSON.parse(atob(state.token.split('.')[1]));
          if (exp && exp * 1000 < Date.now()) {
            state.token = null;
            state.employee = null;
            state.restaurant = null;
            state.isAuthenticated = false;
            state.sessionExpired = true;
          }
        } catch {
          // unreadable token — treat as signed out
          state.token = null;
          state.isAuthenticated = false;
        }
      },
    }
  )
);
